// src/components/admin/Users.tsx
import React, { useEffect, useMemo, useState } from "react";

// ===== 型定義 =====
type User = {
  id: number;
  userId: string;
  name: string;
  lineName: string;
  email: string;
  createdAt: string;
  purchasePoint: number;
  bonusPoint: number;
  status: "有効" | "停止";
  affiliateId: string;
  affiliateName: string;
};

type ApiUser = {
  id: number | string;
  userId?: string;
  name?: string;
  lineName?: string; // ★ /api/admin/users の lineName
  lineDisplayName?: string; // 将来の互換用
  email?: string;
  createdAt?: string;
  purchasePoint?: number;
  bonusPoint?: number;
  isActive?: boolean; // false: 停止 / true or undefined: 有効
  affiliateId?: string;
  affiliateName?: string;
};

type OrderRow = {
  id: string;
  date: string;
  planId: number;
  planName: string;
  amount: number;
  affiliateId?: string | null;
  affiliateName?: string | null;
};

type PointsRow = {
  id: string;
  date: string;
  type: string;
  value: number;
  memo?: string;
};

// ヘッダーと同じ会員ID整形ロジック
const formatMemberId = (id?: number, userId?: string): string => {
  const base = id != null ? String(id) : userId != null ? String(userId) : "";

  if (!base) return "-";

  // すでに数字だけならそのまま
  if (/^\d+$/.test(base)) return base;

  // それ以外（LINEのUd〜など）は末尾6桁から「51 + 6桁」に変換
  const digits = base.replace(/\D/g, "");
  if (!digits) return "-";

  return "51" + digits.slice(-6).padStart(6, "0");
};

const fmtYmd = (s?: string | null) => {
  if (!s) return "";
  const d = new Date(s);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const mapApiUserToUi = (r: ApiUser): User => ({
  id: Number(r.id),
  userId: r.userId || `U${String(r.id).padStart(4, "0")}`,
  // 表示用「名前」: 明示 name → LINE名 → displayName
  name: r.name || r.lineName || r.lineDisplayName || "-",
  // LINE名列: lineName を最優先で使う
  lineName: r.lineName || r.lineDisplayName || r.name || "-",
  email: r.email || "",
  createdAt: fmtYmd(r.createdAt),
  purchasePoint: r.purchasePoint ?? 0,
  bonusPoint: r.bonusPoint ?? 0,
  status: r.isActive === false ? "停止" : "有効",
  affiliateId: r.affiliateId || "",
  affiliateName: r.affiliateName || "",
});

const PAGE_SIZE = 50;

// ★ ページ番号（…含む）生成：1 2 … 5 6 [7] 8 9 … 20
const buildPageItems = (
  current: number,
  total: number,
  windowSize = 5
): Array<number | "..."> => {
  const items: Array<number | "..."> = [];
  if (total <= 1) return [1];

  const half = Math.floor(windowSize / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(total, start + windowSize - 1);
  start = Math.max(1, end - windowSize + 1);

  // 先頭
  items.push(1);

  if (start > 2) items.push("...");

  for (let p = Math.max(2, start); p <= Math.min(total - 1, end); p++) {
    items.push(p);
  }

  if (end < total - 1) items.push("...");

  // 末尾
  if (total >= 2) items.push(total);

  // 重複の整理（total=2などで [1,2,2] を避ける）
  const dedup: Array<number | "..."> = [];
  for (const it of items) {
    const last = dedup[dedup.length - 1];
    if (it === last) continue;
    dedup.push(it);
  }
  return dedup;
};

export default function Users() {
  const [users, setUsers] = useState<User[]>([]);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [purchaseDelta, setPurchaseDelta] = useState(100);
  const [bonusDelta, setBonusDelta] = useState(100);
  const [kw, setKw] = useState("");
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  // アフィリエイター編集用
  const [editingUserId, setEditingUserId] = useState<number | null>(null);
  const [editingAffiliateValue, setEditingAffiliateValue] =
    useState<string>("");
  const [savingAffiliateUserId, setSavingAffiliateUserId] = useState<
    number | null
  >(null);

  // 検索フィルタ
  const filtered = useMemo(() => {
    const k = kw.trim().toLowerCase();
    if (!k) return users;
    return users.filter((u) =>
      [u.name, u.email, u.userId, u.lineName, u.affiliateName, u.affiliateId].some(
        (v) => (v || "").toLowerCase().includes(k)
      )
    );
  }, [users, kw]);

  // ページング計算
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);

  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filtered.slice(start, end);
  }, [filtered, currentPage]);

  // このページの全件チェック状態
  const isAllChecked =
    pagedUsers.length > 0 && pagedUsers.every((u) => checkedIds.includes(u.id));

  const reload = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/users`, {
        credentials: "include",
        cache: "no-store",
      });

      const json = await res.json();
      const rows: ApiUser[] = Array.isArray(json) ? json : json?.data || [];
      setUsers(rows.map(mapApiUserToUi));
      setPage(1); // 再読み込み時は1ページ目へ
      setCheckedIds([]);
    } catch (e) {
      console.warn("GET /api/admin/users error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    reload();
  }, []);

  // ====== 一斉付与 ======
  const grantBulk = async () => {
    if (checkedIds.length === 0) {
      alert("付与対象を選択してください");
      return;
    }
    try {
      const res = await fetch(`/api/admin/users/grant`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ids: checkedIds,
          purchaseDelta: Number(purchaseDelta) || 0,
          bonusDelta: Number(bonusDelta) || 0,
          memo: "管理画面一斉付与",
        }),
      });

      if (!res.ok) throw new Error("NG");
      alert("付与が完了しました");
      setCheckedIds([]);
      reload();
    } catch {
      alert("サーバ更新に失敗しました");
    }
  };

  // ====== 状態切替 ======
  const toggleStatus = async (user: User) => {
    const optimistic = user.status === "停止" ? "有効" : "停止";
    setUsers((arr) =>
      arr.map((u) => (u.id === user.id ? { ...u, status: optimistic } : u))
    );
    try {
      const res = await fetch(`/api/admin/users/${user.id}/status`, {
        method: "PATCH",
        credentials: "include",
      });

      if (!res.ok) throw new Error("NG");
    } catch {
      setUsers((arr) =>
        arr.map((u) => (u.id === user.id ? { ...u, status: user.status } : u))
      );
      alert("更新に失敗しました");
    }
  };

  // ====== 購入履歴を見る ======
  const viewOrders = async (userId: number) => {
    try {
      const res = await fetch(`/api/admin/users/${userId}/orders`, {
        credentials: "include",
      });

      const rows: OrderRow[] = await res.json();
      if (!Array.isArray(rows) || rows.length === 0) {
        alert("購入履歴はありません");
        return;
      }
      const lines = rows.map(
        (r) =>
          `${r.date.slice(0, 10)}  ${r.id}  ${r.planName}  ${Number(
            r.amount
          ).toLocaleString()}pt  ` +
          (r.affiliateName
            ? `（AF:${r.affiliateName}${
                r.affiliateId ? `/${r.affiliateId}` : ""
              }）`
            : "")
      );
      alert(lines.join("\n"));
    } catch {
      alert("購入履歴の取得に失敗しました");
    }
  };

  // ====== ポイント付与履歴を見る ======
  const viewPoints = async (userId: number) => {
    try {
      // MyPage API から pointsLedger を取得する
      const res = await fetch(`/api/user/${userId}/mypage`, {
        credentials: "include",
      });

      if (!res.ok) throw new Error("NG");

      const json = await res.json();
      const rows: PointsRow[] = Array.isArray(json.points) ? json.points : [];

      if (!rows.length) {
        alert("ポイント付与履歴はありません");
        return;
      }

      const lines = rows.map((r) => {
        const d = (r.date || "").slice(0, 10);
        const v = Number(r.value || 0);
        const sign = v > 0 ? "+" : "";
        return `${d}  ${r.type}  ${sign}${v.toLocaleString()}pt  ${
          r.memo || ""
        }`;
      });

      alert(lines.join("\n"));
    } catch (e) {
      console.error(e);
      alert("ポイント付与履歴の取得に失敗しました");
    }
  };

  // ====== アフィリエイター更新 ======
  const startEditAffiliate = (user: User) => {
    setEditingUserId(user.id);
    setEditingAffiliateValue(user.affiliateId || "");
  };

  const cancelEditAffiliate = () => {
    setEditingUserId(null);
    setEditingAffiliateValue("");
  };

  const saveAffiliate = async (user: User) => {
    const newVal = editingAffiliateValue.trim();

    if (!newVal) {
      const ok = window.confirm(
        "媒体者IDを空にするとアフィリエイター紐付けを解除します。よろしいですか？"
      );
      if (!ok) return;
    }

    try {
      setSavingAffiliateUserId(user.id);

      const res = await fetch(`/api/admin/users/${user.id}/affiliate`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          affiliateId: newVal || null,
        }),
      });

      // ★ サーバーからのエラー内容を拾って表示するように変更
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(text || `HTTP ${res.status}`);
      }

      await reload();
      setEditingUserId(null);
      setEditingAffiliateValue("");
    } catch (e: any) {
      console.error(e);
      alert(
        `アフィリエイターの更新に失敗しました\n${
          e?.message ? String(e.message) : ""
        }`
      );
    } finally {
      setSavingAffiliateUserId(null);
    }
  };

  // ★ ページ番号リスト
  const pageItems = useMemo(
    () => buildPageItems(currentPage, totalPages, 5),
    [currentPage, totalPages]
  );

  return (
    <div style={{ maxWidth: 1300, margin: "40px auto" }}>
      <h2 style={{ marginBottom: 16 }}>👤 ユーザー管理</h2>

      {/* 検索 */}
      <div
        style={{
          display: "flex",
          gap: 8,
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <input
          type="text"
          placeholder="LINE名・アフィリエイターで検索"
          value={kw}
          onChange={(e) => {
            setKw(e.target.value);
            setPage(1); // 検索条件変更で1ページ目に戻す
          }}
          style={{ width: 360, padding: 8 }}
        />
        <button onClick={reload}>再読み込み</button>
      </div>

      {/* 一斉付与 */}
      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginBottom: 16,
        }}
      >
        <label>
          購入PT一斉付与：
          <input
            type="number"
            value={purchaseDelta}
            onChange={(e) => setPurchaseDelta(Number(e.target.value))}
            style={{ width: 80, marginLeft: 6 }}
          />
        </label>
        <label>
          付与PT一斉付与：
          <input
            type="number"
            value={bonusDelta}
            onChange={(e) => setBonusDelta(Number(e.target.value))}
            style={{ width: 80, marginLeft: 6 }}
          />
        </label>

        {/* 全件選択ボタン（このページ分） */}
        <button
          type="button"
          onClick={() =>
            setCheckedIds(
              isAllChecked
                ? checkedIds.filter((id) => !pagedUsers.some((u) => u.id === id))
                : Array.from(
                    new Set([...checkedIds, ...pagedUsers.map((u) => u.id)])
                  )
            )
          }
          style={{ padding: "8px 14px" }}
        >
          {isAllChecked ? "このページ選択解除" : "このページ全件選択"}
        </button>

        <button onClick={grantBulk} style={{ padding: "8px 14px" }}>
          チェックしたユーザーに一斉付与
        </button>
      </div>

      {/* テーブル */}
      {loading ? (
        <p>読み込み中…</p>
      ) : (
        <>
          <table
            style={{
              width: "100%",
              borderCollapse: "separate",
              borderSpacing: 0,
              background: "#fff",
              boxShadow: "0 0 10px #eee",
            }}
          >
            <thead>
              <tr
                style={{
                  background: "#e6e9ee",
                  fontWeight: "bold",
                  borderTop: "2px solid #888",
                }}
              >
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    background: "#f5f6f8",
                    width: 80,
                    textAlign: "center",
                  }}
                >
                  選択
                  <br />
                  <input
                    type="checkbox"
                    checked={isAllChecked}
                    onChange={() =>
                      setCheckedIds(
                        isAllChecked
                          ? checkedIds.filter(
                              (id) => !pagedUsers.some((u) => u.id === id)
                            )
                          : Array.from(
                              new Set([
                                ...checkedIds,
                                ...pagedUsers.map((u) => u.id),
                              ])
                            )
                      )
                    }
                    style={{
                      width: 16,
                      height: 16,
                      margin: 0,
                      padding: 0,
                      display: "inline-block",
                    }}
                  />
                </th>
                <th style={{ border: "1px solid #ddd", padding: 4 }}>会員ID</th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    width: 150, // ★ LINE名を少し狭く
                  }}
                >
                  LINE名
                </th>
                {/* メール列は削除 */}
                <th style={{ border: "1px solid #ddd", padding: 8 }}>登録日</th>
                <th
                  style={{
                    border: "1px solid #1976d2",
                    padding: 8,
                    background: "#eaf4fc",
                  }}
                >
                  購入PT
                </th>
                <th
                  style={{
                    border: "1px solid #f39c12",
                    padding: 8,
                    background: "#fff9ec",
                  }}
                >
                  付与PT
                </th>
                <th style={{ border: "1px solid #ddd", padding: 8 }}>状態</th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    width: 260, // ★ アフィ列を少し広く（2行にならないように）
                  }}
                >
                  アフィリエイター
                </th>
                <th style={{ border: "1px solid #ddd", padding: 8 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {pagedUsers.map((user) => {
                const isEditing = editingUserId === user.id;
                const isSaving = savingAffiliateUserId === user.id;

                return (
                  <tr
                    key={user.id}
                    style={{
                      borderBottom: "1px solid #e0e2e7",
                      height: 26, // 行を少し詰める
                    }}
                  >
                    <td style={{ textAlign: "center", width: 80 }}>
                      <input
                        type="checkbox"
                        checked={checkedIds.includes(user.id)}
                        onChange={() =>
                          setCheckedIds((prev) =>
                            prev.includes(user.id)
                              ? prev.filter((x) => x !== user.id)
                              : [...prev, user.id]
                          )
                        }
                        style={{
                          width: 16,
                          height: 16,
                          margin: 0,
                          padding: 0,
                          display: "inline-block",
                        }}
                      />
                    </td>
                    <td>{formatMemberId(user.id, user.userId)}</td>
                    <td>{user.lineName}</td>
                    {/* メール列削除 */}
                    <td>{user.createdAt}</td>
                    <td style={{ textAlign: "right", color: "#1976d2" }}>
                      {user.purchasePoint.toLocaleString()}
                    </td>
                    <td style={{ textAlign: "right", color: "#f39c12" }}>
                      {user.bonusPoint.toLocaleString()}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button
                        style={{
                          background:
                            user.status === "有効" ? "#3ad45a" : "#aaa",
                          color: "#fff",
                          border: "none",
                          borderRadius: 4,
                          padding: "2px 8px",
                          fontSize: 12,
                        }}
                        onClick={() => toggleStatus(user)}
                      >
                        {user.status}
                      </button>
                    </td>
                    <td style={{ padding: 4, minWidth: 260 }}>
                      {/* アフィリエイター列 */}
                      {!isEditing ? (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            gap: 8,
                          }}
                        >
                          {/* 左側：常に表示されるアフィ名 / ID */}
                          <div>
                            <b>{user.affiliateName || "-"}</b>{" "}
                            {user.affiliateId && (
                              <span style={{ color: "#888" }}>
                                （ID: {user.affiliateId}）
                              </span>
                            )}
                          </div>

                          {/* 右側：列の右端に固定される更新ボタン */}
                          <div style={{ minWidth: 60, textAlign: "right" }}>
                            <button
                              type="button"
                              onClick={() => startEditAffiliate(user)}
                              style={{
                                padding: "4px 8px",
                                fontSize: 11,
                                borderRadius: 4,
                                border: "1px solid #ccc",
                                background: "#fff",
                                whiteSpace: "nowrap",
                              }}
                            >
                              更新
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 4,
                          }}
                        >
                          {/* 1行目：現在のアフィ表示（常に表示） */}
                          <div>
                            <b>{user.affiliateName || "-"}</b>{" "}
                            {user.affiliateId && (
                              <span style={{ color: "#888" }}>
                                （ID: {user.affiliateId}）
                              </span>
                            )}
                          </div>

                          {/* 2行目：媒体者ID入力＋ボタン群（横並び） */}
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 6,
                            }}
                          >
                            <input
                              type="text"
                              placeholder="媒体者ID"
                              value={editingAffiliateValue}
                              onChange={(e) =>
                                setEditingAffiliateValue(e.target.value)
                              }
                              style={{ width: 120, padding: "4px 6px" }}
                            />
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                              }}
                            >
                              <button
                                type="button"
                                disabled={isSaving}
                                onClick={() => saveAffiliate(user)}
                                style={{
                                  padding: "4px 8px",
                                  fontSize: 11,
                                  borderRadius: 4,
                                  border: "1px solid #4caf50",
                                  background: isSaving ? "#c8e6c9" : "#e8f5e9",
                                  cursor: isSaving ? "default" : "pointer",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {isSaving ? "保存中…" : "保存"}
                              </button>
                              <button
                                type="button"
                                disabled={isSaving}
                                onClick={cancelEditAffiliate}
                                style={{
                                  padding: "4px 8px",
                                  fontSize: 11,
                                  borderRadius: 4,
                                  border: "1px solid #ccc",
                                  background: "#fafafa",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                キャンセル
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </td>

                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      <button
                        onClick={() => viewOrders(user.id)}
                        style={{ marginRight: 4 }}
                      >
                        購入履歴
                      </button>
                      <button onClick={() => viewPoints(user.id)}>
                        ポイント付与履歴
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={9} // ★ メール列削除に合わせて 9 列
                    style={{ textAlign: "center", color: "#aaa" }}
                  >
                    データがありません
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* ▼ ページネーション */}
          <div
            style={{
              marginTop: 16,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1}
              style={{
                padding: "4px 10px",
                borderRadius: 4,
                border: "1px solid #ccc",
                background: currentPage <= 1 ? "#eee" : "#fff",
                cursor: currentPage <= 1 ? "default" : "pointer",
              }}
            >
              前へ
            </button>

            {/* ★ ページ番号ボタン */}
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              {pageItems.map((it, idx) =>
                it === "..." ? (
                  <span key={`dots-${idx}`} style={{ padding: "0 4px" }}>
                    …
                  </span>
                ) : (
                  <button
                    key={it}
                    type="button"
                    onClick={() => setPage(it)}
                    disabled={it === currentPage}
                    style={{
                      minWidth: 34,
                      padding: "4px 10px",
                      borderRadius: 4,
                      border: "1px solid #ccc",
                      background: it === currentPage ? "#111" : "#fff",
                      color: it === currentPage ? "#fff" : "#000",
                      cursor: it === currentPage ? "default" : "pointer",
                      fontWeight: it === currentPage ? "bold" : "normal",
                    }}
                    aria-label={`ページ ${it}`}
                  >
                    {it}
                  </button>
                )
              )}
            </div>

            <span>
              {currentPage} / {totalPages} ページ（全 {filtered.length} 件）
            </span>

            <button
              type="button"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages}
              style={{
                padding: "4px 10px",
                borderRadius: 4,
                border: "1px solid #ccc",
                background: currentPage >= totalPages ? "#eee" : "#fff",
                cursor: currentPage >= totalPages ? "default" : "pointer",
              }}
            >
              次へ
            </button>
          </div>
        </>
      )}
    </div>
  );
}